/**
 * B549 leg (2) — the buyer-surface group interrupt (444:2) and inquiry
 * form (446:2), pure derivations. The frames are the spec: at the
 * channel's group threshold the booking sheet's calendar column becomes
 * the GROUP BOOKING panel, and REQUEST A GROUP QUOTE walks into the
 * inquiry form (`group.inquiry`). The threshold is the mint's wire fact
 * (session.guest, leg 1); everything here derives from it and the card.
 *
 * Copy rules, each an honest derivation:
 *  - the title speaks the tenant's OWN threshold, never a hardcoded 20;
 *  - the keep-door names threshold − 1 — the largest party self-service
 *    still sells (the cart wall refuses ≥ threshold, leg 1);
 *  - the amber note tells the truth about that wall rather than promising
 *    a public-price checkout the platform would refuse.
 */
import { GROUP_INQUIRY_KINDS } from "@transact/contracts";
import type { Composition } from "./composition.js";
import { quantityOf, totalQuantity } from "./composition.js";

export type GroupInquiryKind = (typeof GROUP_INQUIRY_KINDS)[number];

/** The interrupt replaces the calendar column at threshold, not above it. */
export function groupInterruptActive(threshold: number | undefined, ticketCount: number): boolean {
  return threshold !== undefined && threshold > 0 && ticketCount >= threshold;
}

/** The largest self-service party: the keep-door's number and the stepper's honest cap. */
export function keepCount(threshold: number): number {
  return Math.max(1, threshold - 1);
}

/**
 * The stepper's cap for one type: room under the threshold plus what this
 * type already holds — the party can REACH the threshold (the frame shows
 * 20, the panel appears) but never pass it.
 */
export function cappedMax(baseMax: number, threshold: number | undefined, ticketCount: number, own: number): number {
  if (threshold === undefined || threshold <= 0) return baseMax;
  return Math.min(baseMax, own + Math.max(0, threshold - ticketCount));
}

/**
 * "or keep 19 tickets" — step the party back under the threshold: largest
 * lines give first, deterministically, until the total is threshold − 1.
 */
export function reduceToKeep(comp: Composition, threshold: number): Composition {
  const target = keepCount(threshold);
  let excess = totalQuantity(comp) - target;
  if (excess <= 0) return comp;
  const entries = Object.entries(comp)
    .filter(([, q]) => q > 0)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const next: Record<string, number> = { ...comp };
  for (const [variantId, q] of entries) {
    if (excess <= 0) break;
    const give = Math.min(q, excess);
    next[variantId] = q - give;
    excess -= give;
  }
  return next;
}

/* ------------------------------------------------------------ the copy */

/** B549 (9) — who replies: the desk's own first name when the channel names one, else the desk. */
export function deskRepliesLead(deskName: string | undefined): string {
  return deskName === undefined ? "The groups desk replies" : `${deskName} at the groups desk replies`;
}

/** The inquiry's one-line promise (the sheet foot, the sticky bar's lead). */
export function deskRepliesWords(deskName: string | undefined): string {
  return `${deskRepliesLead(deskName)} within one business day with a quote`;
}

/** Step 1 of WHAT HAPPENS NEXT (446:2: "Maya sends a quote"). */
export function deskSendsWords(deskName: string | undefined): string {
  return deskName === undefined ? "The desk sends a quote" : `${deskName} sends a quote`;
}

export function groupPanelTitle(threshold: number): string {
  return `${threshold} or more? That’s a group.`;
}

export function groupPanelBlurb(threshold: number, businessName: string | undefined): string {
  const who = businessName ?? "This venue";
  return `${who} has a groups desk for schools, companies and tours — group rates, one invoice, and a coordinator who plans the visit with you. Tell us about your party of ${threshold}+ and a quote comes back, usually the same day.`;
}

export function groupNoteText(threshold: number): string {
  return `Prefer to buy ${threshold} tickets right now at the public price? Online checkout stops at ${keepCount(threshold)} so parties of ${threshold} or more always get the group rate — keep ${keepCount(threshold)} below, or send the inquiry and let the desk price all ${threshold}.`;
}

/** "20 adults · above the group threshold — group pricing applies" (444:2). */
export function groupFooterLine(party: string | undefined, ticketCount: number): string {
  return `${party ?? `${ticketCount} tickets`} · above the group threshold — group pricing applies`;
}

export function keepTicketsLabel(threshold: number): string {
  return `or keep ${keepCount(threshold)} tickets and continue ›`;
}

/** "20+ guests · public $32.00" — the group rate itself is the desk's to quote. */
export function groupExperienceLine(threshold: number, publicFrom: string | undefined): string {
  return publicFrom === undefined ? `${threshold}+ guests` : `${threshold}+ guests · public ${publicFrom}`;
}

/* ------------------------------------------------------- the form facts */

export interface GroupInquiryDraft {
  readonly organisation: string;
  readonly headcount: string;
  readonly kind: GroupInquiryKind;
  readonly contactName: string;
  readonly contactEmail: string;
  readonly contactPhone: string;
  readonly preferredDate: string;
  readonly notes: string;
}

/** The form opens knowing what the party already said: the count and the day. */
export function seedInquiryDraft(ticketCount: number, preferredDate: string | undefined): GroupInquiryDraft {
  return {
    organisation: "",
    headcount: ticketCount > 0 ? String(ticketCount) : "",
    kind: "school",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    preferredDate: preferredDate ?? "",
    notes: "",
  };
}

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

/** What still gates SEND, in buyer words — empty means the door is armed. */
export function inquiryGaps(d: GroupInquiryDraft): readonly string[] {
  const gaps: string[] = [];
  if (d.organisation.trim() === "") gaps.push("who the group is");
  const count = Number(d.headcount);
  if (!Number.isSafeInteger(count) || count < 1) gaps.push("how many are coming");
  if (d.contactName.trim() === "") gaps.push("your name");
  if (!EMAIL_SHAPE.test(d.contactEmail.trim())) gaps.push("an email we can reply to");
  return gaps;
}

/** The wire body, built only when the gaps are empty (the caller checks). */
export function inquiryBody(d: GroupInquiryDraft, channelId: string): Record<string, unknown> {
  return {
    channelId,
    organisation: d.organisation.trim(),
    contactName: d.contactName.trim(),
    contactEmail: d.contactEmail.trim(),
    ...(d.contactPhone.trim() !== "" ? { contactPhone: d.contactPhone.trim() } : {}),
    ...(DATE_SHAPE.test(d.preferredDate) ? { preferredDate: d.preferredDate } : {}),
    headcount: Number(d.headcount),
    kind: d.kind,
    ...(d.notes.trim() !== "" ? { notes: d.notes.trim() } : {}),
  };
}
